import { useCallback } from 'react';
import {
  VALUE_CHAIN_MAP_CUSTOM_NODES,
  VALUE_CHAIN_UNIT_TYPES,
} from 'constants/interfaces';

import type { Node } from '@xyflow/react';
import type React from 'react';
import type IBalanceSheetAssets from 'store/slices/valueChainMap/interfaces/IBalanceSheetAssets';
import type ICustomer from 'store/slices/valueChainMap/interfaces/ICustomer';
import type IInputProduct from 'store/slices/valueChainMap/interfaces/IInputProduct';
import type { IProfitCenter } from 'store/slices/valueChainMap/interfaces/IProfitCenter';
import type ISupplier from 'store/slices/valueChainMap/interfaces/ISupplier';
import type { IUseValueChainNodeInteractionsParams } from '../interfaces/IUseValueChainNodeInteractions';

const useValueChainNodeInteractions = ({
  openProfitCenterModal,
  setSelectedProfitCenter,
  setCurrentTargetSwimlaneId,
  openCustomerModal,
  setSelectedCustomer,
  openSupplierModal,
  setSelectedSupplier,
  openBalanceSheetAssetsModal,
  setSelectedBalanceSheetAssets,
  openInputProductModal,
  setSelectedInputProduct,
}: IUseValueChainNodeInteractionsParams) => {
  const onNodeDoubleClick = useCallback((event: React.MouseEvent, node: Node) => {
    // Ignore double click on swimlane nodes
    if (node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE) {
      return;
    }

    // Fall back to the unit recordId when the record itself has no id for the
    // request (e.g. grouping-mode board unit cards carry the id in recordId).
    const fallbackRecordId = node.data.recordId as number | undefined;
    const withFallbackId = <TRecord extends { id: number }>(record?: TRecord): TRecord | undefined => {
      if (record?.id || !fallbackRecordId) {
        return record;
      }

      return { ...(record || {}), id: fallbackRecordId } as TRecord;
    };

    if (node.data.type === VALUE_CHAIN_MAP_CUSTOM_NODES.PROFIT_CENTER) {
      const records = node.data.records as IProfitCenter[] || [];
      setSelectedProfitCenter(withFallbackId(records[0]));
      setCurrentTargetSwimlaneId(Number((node?.data?.swimlaneId as string)?.split('_')[1]));
      openProfitCenterModal();
    }

    if (node.data.type === VALUE_CHAIN_MAP_CUSTOM_NODES.CUSTOMER) {
      const records = node.data.records as ICustomer[] || [];
      setSelectedCustomer(withFallbackId(records[0]));
      setCurrentTargetSwimlaneId(Number((node?.data?.swimlaneId as string)?.split('_')[1]));
      openCustomerModal();
    }

    if (node.data.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SUPPLIER) {
      const records = node.data.records as ISupplier[] || [];
      setSelectedSupplier(withFallbackId(records[0]));
      setCurrentTargetSwimlaneId(Number((node?.data?.swimlaneId as string)?.split('_')[1]));
      openSupplierModal();
    }

    if (node.data.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BALANCE_SHEET_ASSET) {
      const records = node.data.records as IBalanceSheetAssets[] || [];
      setSelectedBalanceSheetAssets(withFallbackId(records[0]));
      setCurrentTargetSwimlaneId(Number((node?.data?.swimlaneId as string)?.split('_')[1]));
      openBalanceSheetAssetsModal();
    }

    if (
      node.data.type === VALUE_CHAIN_UNIT_TYPES.PRODUCT
      || node.data.type === VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT
    ) {
      const records = node.data.records as IInputProduct[] || [];
      setSelectedInputProduct(withFallbackId(records[0]));
      setCurrentTargetSwimlaneId(Number((node?.data?.swimlaneId as string)?.split('_')[1]));
      openInputProductModal();
    }
  }, [setSelectedProfitCenter, openProfitCenterModal, setCurrentTargetSwimlaneId,
    openCustomerModal, setSelectedCustomer, openSupplierModal, setSelectedSupplier,
    openBalanceSheetAssetsModal, setSelectedBalanceSheetAssets, openInputProductModal, setSelectedInputProduct]);

  return { onNodeDoubleClick };
};

export default useValueChainNodeInteractions;
